import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { RoadmapProject, TimeHorizon } from '../types';
import {
  getStatusClass,
  getStatusLabel,
  getProjectQuarterInfo,
  getInitials,
} from '../utils/roadmapUtils';
import { bulkUpdateTasks } from '../../../services/asanaService';

interface HorizonViewProps {
  projects: RoadmapProject[];
  onProjectClick: (gid: string) => void;
  searchQuery?: string;
  projectId?: string;
}

// ─── Horizon config ───────────────────────────────────────────────────────────

interface HorizonConfig {
  horizon: TimeHorizon;
  label: string;
  sublabel: string;
  accentColor: string;
  defaultOpen: boolean;
}

const HORIZON_CONFIG: HorizonConfig[] = [
  {
    horizon: 'NOW',
    label: 'NOW',
    sublabel: 'Actively being built — In Progress, Blocked, or Delayed',
    accentColor: '#34c759',
    defaultOpen: true,
  },
  {
    horizon: 'NEXT',
    label: 'NEXT',
    sublabel: 'Committed and queued — work starts soon',
    accentColor: '#007aff',
    defaultOpen: true,
  },
  {
    horizon: 'LATER',
    label: 'LATER',
    sublabel: 'Not yet committed, or intentionally paused',
    accentColor: '#8e8e93',
    defaultOpen: false,
  },
  {
    horizon: 'COMPLETE',
    label: 'COMPLETE',
    sublabel: 'Terminal — Completed or Cancelled',
    accentColor: '#636366',
    defaultOpen: false,
  },
];

/** Maps a horizon to the default Asana status written on drop */
const HORIZON_TO_STATUS: Record<TimeHorizon, string> = {
  NOW:      'In Progress',
  NEXT:     'Not Started',
  LATER:    'On Hold',
  COMPLETE: 'Completed',
};

// ─── Horizon mapping ──────────────────────────────────────────────────────────

const getHorizon = (project: RoadmapProject): TimeHorizon => {
  const s = (project.status || '').toLowerCase().trim();
  if (s.includes('in progress') || s.includes('blocked') || s.includes('delayed')) return 'NOW';
  if (s.includes('not started') || s.includes('in review')) return 'NEXT';
  if (s.includes('complet') || s === 'done' || s.includes('cancel')) return 'COMPLETE';
  return 'LATER'; // On Hold + blank
};

// ─── Highlight helper ─────────────────────────────────────────────────────────

const highlight = (text: string, query: string): React.ReactNode => {
  if (!query || !text) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="search-highlight">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
};

// ─── Info Popover ─────────────────────────────────────────────────────────────

const STATUS_GUIDE = [
  { cls: 'in-progress', label: 'In Progress',  desc: 'Work has started' },
  { cls: 'blocked',     label: 'Blocked',       desc: 'Waiting on another team or dependency' },
  { cls: 'delayed',     label: 'Delayed',       desc: 'Missed planned date — still being worked on' },
  { cls: 'in-review',   label: 'In Review',     desc: 'PM or engineering is reviewing / estimating' },
  { cls: 'not-started', label: 'Not Started',   desc: 'Approved and queued — work hasn\'t begun' },
  { cls: 'on-hold',     label: 'On Hold',       desc: 'Intentionally paused — not being worked on' },
  { cls: 'complete',    label: 'Completed',     desc: 'Shipped and done' },
  { cls: 'cancelled',   label: 'Cancelled',     desc: 'Won\'t be built' },
];

const InfoPopover: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const keyHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [onClose]);

  return (
    <div className="horizon-info-popover" ref={ref} role="dialog" aria-label="How this view works">
      <div className="horizon-info-header">
        <strong>How this view works</strong>
        <button className="horizon-info-close" onClick={onClose} type="button" aria-label="Close">✕</button>
      </div>

      <p className="horizon-info-intro">
        Cards are grouped by where they are in your delivery pipeline, not just their quarter.
        Drag a card between lanes to move it and optionally update its status in Asana.
      </p>

      <div className="horizon-info-lanes">
        {HORIZON_CONFIG.map(c => (
          <div key={c.horizon} className="horizon-info-lane">
            <span className="horizon-info-lane-dot" style={{ background: c.accentColor }} />
            <div>
              <strong style={{ color: c.accentColor }}>{c.label}</strong>
              <span className="horizon-info-lane-desc"> — {c.sublabel}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="horizon-info-divider" />

      <p className="horizon-info-section-title">Status guide</p>
      <div className="horizon-info-statuses">
        {STATUS_GUIDE.map(s => (
          <div key={s.cls} className="horizon-info-status-row">
            <span className={`status-badge ${s.cls} horizon-info-badge`}>
              <span className="status-badge-dot" />
              {s.label}
            </span>
            <span className="horizon-info-status-desc">{s.desc}</span>
          </div>
        ))}
      </div>

      <div className="horizon-info-divider" />

      <p className="horizon-info-workflow-title">PM workflow</p>
      <ol className="horizon-info-workflow">
        <li>New request arrives → set to <strong>In Review</strong> (PM triage)</li>
        <li>Estimate complete, approved → set to <strong>Not Started</strong></li>
        <li>Work begins → set to <strong>In Progress</strong></li>
        <li>External blocker → set to <strong>Blocked</strong></li>
        <li>Missed date → set to <strong>Delayed</strong></li>
        <li>Done → set to <strong>Completed</strong></li>
        <li>Won't build → set to <strong>Cancelled</strong></li>
      </ol>
    </div>
  );
};

// ─── Confirmation Toast ───────────────────────────────────────────────────────

interface PendingConfirmation {
  gid: string;
  projectName: string;
  toHorizon: TimeHorizon;
  toStatus: string;
}

interface ConfirmToastProps {
  pending: PendingConfirmation;
  isSaving: boolean;
  saveError: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmToast: React.FC<ConfirmToastProps> = ({
  pending, isSaving, saveError, onConfirm, onCancel,
}) => {
  const config = HORIZON_CONFIG.find(c => c.horizon === pending.toHorizon)!;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    timerRef.current = setTimeout(onCancel, 12000);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [onCancel]);

  return (
    <div className="horizon-confirm-toast" role="dialog" aria-live="polite">
      <div className="horizon-confirm-toast-body">
        <div className="horizon-confirm-icon" style={{ background: config.accentColor }} />
        <div className="horizon-confirm-text">
          <strong>Move to {config.label}?</strong>
          <span className="horizon-confirm-project">"{pending.projectName}"</span>
          <span className="horizon-confirm-sub">
            Status will be set to <em>{pending.toStatus}</em> in Asana
          </span>
        </div>
      </div>

      {saveError && (
        <div className="horizon-confirm-error">⚠ {saveError}</div>
      )}

      <div className="horizon-confirm-actions">
        <button
          className="horizon-confirm-btn-primary"
          onClick={onConfirm}
          disabled={isSaving}
          type="button"
        >
          {isSaving && <span className="horizon-saving-spinner" aria-label="Saving…" />}
          {isSaving ? 'Saving…' : 'Save to Asana'}
        </button>
        <button
          className="horizon-confirm-btn-secondary"
          onClick={onCancel}
          disabled={isSaving}
          type="button"
        >
          Keep local only
        </button>
      </div>
    </div>
  );
};

// ─── Project Card ─────────────────────────────────────────────────────────────

interface HorizonCardProps {
  project: RoadmapProject;
  isDragging: boolean;
  isSaving: boolean;
  isSaved: boolean;
  onClick: () => void;
  onDragStart: (gid: string) => void;
  onDragEnd: () => void;
  searchQuery?: string;
}

const HorizonCard: React.FC<HorizonCardProps> = ({
  project, isDragging, isSaving, isSaved, onClick, onDragStart, onDragEnd, searchQuery = '',
}) => {
  const statusClass = getStatusClass(project.status);
  const statusLabel = getStatusLabel(project.status);
  const quarterInfo = getProjectQuarterInfo(project);
  const initials = project.assignee ? getInitials(project.assignee.name) : null;

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const s = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const d = new Date(`${s}T00:00:00`);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div
      className={`horizon-card${isDragging ? ' dragging' : ''}${isSaving ? ' saving' : ''}${isSaved ? ' saved' : ''}`}
      draggable
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-grabbed={isDragging}
      onKeyDown={e => e.key === 'Enter' && onClick()}
      onDragStart={e => {
        e.stopPropagation();
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', project.gid);
        onDragStart(project.gid);
      }}
      onDragEnd={onDragEnd}
    >
      {isSaving && (
        <div className="horizon-card-saving-overlay">
          <span className="horizon-saving-spinner" />
        </div>
      )}
      {isSaved && <div className="horizon-card-saved-badge">✓ Saved</div>}

      <div className="horizon-card-top">
        <span className={`status-badge ${statusClass}`}>
          <span className="status-badge-dot" aria-hidden="true" />
          {statusLabel}
        </span>
        {quarterInfo && <span className="quarter-pill-small">{quarterInfo.key}</span>}
      </div>

      <div className="horizon-card-name">
        {highlight(project.name, searchQuery)}
      </div>

      {project.program && project.program !== 'Unassigned' && (
        <div className="horizon-card-program">
          {highlight(project.program, searchQuery)}
        </div>
      )}

      {project.launch_date && (
        <div className="horizon-card-date">
          <span className="horizon-card-date-label">Launch</span>
          {formatDate(project.launch_date)}
        </div>
      )}

      {project.assignee && (
        <div className="horizon-card-footer">
          <div className="assignee-avatar" title={project.assignee.name}>{initials}</div>
          <span className="assignee-name-small">{project.assignee.name}</span>
        </div>
      )}
    </div>
  );
};

// ─── Horizon Section ──────────────────────────────────────────────────────────

interface HorizonSectionProps {
  config: HorizonConfig;
  projects: RoadmapProject[];
  isDragOver: boolean;
  isDraggingActive: boolean;
  draggingGid: string | null;
  savingGid: string | null;
  savedGids: Set<string>;
  onProjectClick: (gid: string) => void;
  onDragStart: (gid: string) => void;
  onDragEnd: () => void;
  onDrop: (targetHorizon: TimeHorizon) => void;
  onDragOver: (horizon: TimeHorizon) => void;
  onDragLeave: () => void;
  searchQuery?: string;
}

const HorizonSection: React.FC<HorizonSectionProps> = ({
  config, projects, isDragOver, isDraggingActive, draggingGid,
  savingGid, savedGids, onProjectClick, onDragStart, onDragEnd,
  onDrop, onDragOver, onDragLeave, searchQuery,
}) => {
  const [isOpen, setIsOpen] = useState(config.defaultOpen);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    onDragOver(config.horizon);
    if (!isOpen) setIsOpen(true);
  }, [config.horizon, isOpen, onDragOver]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) onDragLeave();
  }, [onDragLeave]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    onDrop(config.horizon);
  }, [config.horizon, onDrop]);

  const isEmpty = projects.length === 0;
  if (isEmpty && !isDraggingActive) return null;

  return (
    <div
      className={`horizon-section${isDragOver ? ' drag-over' : ''}${isDraggingActive ? ' drag-active' : ''}`}
      id={`horizon-${config.horizon.toLowerCase()}`}
      style={{ '--horizon-accent': config.accentColor } as React.CSSProperties}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div
        className="horizon-section-header"
        onClick={() => setIsOpen(o => !o)}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        onKeyDown={e => e.key === 'Enter' && setIsOpen(o => !o)}
      >
        <div className="horizon-accent-bar" />
        <div className="horizon-header-content">
          <div className="horizon-header-left">
            <span className="horizon-label" style={{ color: config.accentColor }}>{config.label}</span>
            <span className="horizon-sublabel">{config.sublabel}</span>
          </div>
          <div className="horizon-header-right">
            <span className="horizon-count" style={{ color: config.accentColor }}>
              {projects.length} {projects.length === 1 ? 'project' : 'projects'}
            </span>
            <span className={`horizon-chevron${isOpen ? ' open' : ''}`} aria-hidden="true">›</span>
          </div>
        </div>
      </div>

      <div className={`horizon-section-body${isOpen ? ' open' : ''}`}>
        {isEmpty ? (
          <div className="horizon-drop-zone-empty">
            <span>Drop here to move to {config.label}</span>
          </div>
        ) : (
          <div className="horizon-cards-grid">
            {projects.map(project => (
              <HorizonCard
                key={project.gid}
                project={project}
                isDragging={draggingGid === project.gid}
                isSaving={savingGid === project.gid}
                isSaved={savedGids.has(project.gid)}
                onClick={() => onProjectClick(project.gid)}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                searchQuery={searchQuery}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main HorizonView ─────────────────────────────────────────────────────────

export const HorizonView: React.FC<HorizonViewProps> = ({
  projects, onProjectClick, searchQuery, projectId,
}) => {
  const [horizonOverrides, setHorizonOverrides] = useState<Map<string, TimeHorizon>>(new Map());
  const [draggingGid, setDraggingGid] = useState<string | null>(null);
  const [dragOverHorizon, setDragOverHorizon] = useState<TimeHorizon | null>(null);
  const [showInfo, setShowInfo] = useState(false);

  // Confirmation + save state
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
  const [savingGid, setSavingGid] = useState<string | null>(null);
  const [savedGids, setSavedGids] = useState<Set<string>>(new Set());
  const [saveError, setSaveError] = useState<string | null>(null);

  const getEffectiveHorizon = useCallback(
    (project: RoadmapProject): TimeHorizon =>
      horizonOverrides.get(project.gid) ?? getHorizon(project),
    [horizonOverrides]
  );

  const grouped = useMemo(() => {
    const map: Record<TimeHorizon, RoadmapProject[]> = { NOW: [], NEXT: [], LATER: [], COMPLETE: [] };
    projects.forEach(p => { map[getEffectiveHorizon(p)].push(p); });
    return map;
  }, [projects, getEffectiveHorizon]);

  const handleDragStart = useCallback((gid: string) => setDraggingGid(gid), []);
  const handleDragEnd = useCallback(() => { setDraggingGid(null); setDragOverHorizon(null); }, []);
  const handleDragOver = useCallback((horizon: TimeHorizon) => setDragOverHorizon(horizon), []);
  const handleDragLeave = useCallback(() => setDragOverHorizon(null), []);

  const handleDrop = useCallback(
    (targetHorizon: TimeHorizon) => {
      if (!draggingGid) return;
      const project = projects.find(p => p.gid === draggingGid);
      if (!project) return;

      const currentHorizon = getEffectiveHorizon(project);
      if (currentHorizon === targetHorizon) {
        setDraggingGid(null);
        setDragOverHorizon(null);
        return;
      }

      setHorizonOverrides(prev => new Map(prev).set(draggingGid, targetHorizon));
      setDraggingGid(null);
      setDragOverHorizon(null);
      setSaveError(null);
      setPendingConfirmation({
        gid: draggingGid,
        projectName: project.name,
        toHorizon: targetHorizon,
        toStatus: HORIZON_TO_STATUS[targetHorizon],
      });
    },
    [draggingGid, projects, getEffectiveHorizon]
  );

  const handleConfirmSave = useCallback(async () => {
    if (!pendingConfirmation) return;
    const { gid, toStatus } = pendingConfirmation;
    setSavingGid(gid);
    setSaveError(null);
    try {
      await bulkUpdateTasks([{ gid, fields: { status: toStatus } as any }], projectId);
      setSavedGids(prev => new Set(prev).add(gid));
      setPendingConfirmation(null);
      setTimeout(() => setSavedGids(prev => { const n = new Set(prev); n.delete(gid); return n; }), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save. Try again.');
    } finally {
      setSavingGid(null);
    }
  }, [pendingConfirmation, projectId]);

  const handleCancelSave = useCallback(() => {
    setPendingConfirmation(null);
    setSaveError(null);
  }, []);

  const handleCloseInfo = useCallback(() => setShowInfo(false), []);

  const isDraggingActive = draggingGid !== null;

  if (projects.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">🔍</div>
        <h3>No projects found</h3>
        <p>Try adjusting your search or filters.</p>
      </div>
    );
  }

  return (
    <div className={`horizon-view${isDraggingActive ? ' is-dragging' : ''}`}>
      {/* Info button + overrides banner row */}
      <div className="horizon-view-toolbar">
        {horizonOverrides.size > 0 && !pendingConfirmation && (
          <div className="horizon-overrides-banner">
            <span>
              {horizonOverrides.size} {horizonOverrides.size === 1 ? 'card' : 'cards'} moved locally
            </span>
            <button
              className="horizon-reset-btn"
              onClick={() => { setHorizonOverrides(new Map()); setSavedGids(new Set()); }}
              type="button"
            >
              Reset
            </button>
          </div>
        )}
        <div className="horizon-info-btn-wrap">
          <button
            className="horizon-info-btn"
            onClick={() => setShowInfo(v => !v)}
            type="button"
            aria-label="How this view works"
            aria-expanded={showInfo}
          >
            ?
          </button>
          {showInfo && <InfoPopover onClose={handleCloseInfo} />}
        </div>
      </div>

      {HORIZON_CONFIG.map(config => (
        <HorizonSection
          key={config.horizon}
          config={config}
          projects={grouped[config.horizon]}
          isDragOver={dragOverHorizon === config.horizon}
          isDraggingActive={isDraggingActive}
          draggingGid={draggingGid}
          savingGid={savingGid}
          savedGids={savedGids}
          onProjectClick={onProjectClick}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          searchQuery={searchQuery}
        />
      ))}

      {pendingConfirmation && (
        <ConfirmToast
          pending={pendingConfirmation}
          isSaving={savingGid === pendingConfirmation.gid}
          saveError={saveError}
          onConfirm={handleConfirmSave}
          onCancel={handleCancelSave}
        />
      )}
    </div>
  );
};

export default HorizonView;